import React from "react";

import { G, Line } from "react-native-svg";

import { BOARD_BG } from "../../theme/boardColors";
import type { BoardLink, BoardPalette } from "./boardModel";
import { edgeStyle } from "./edgeStyle";
import { strokeWidthProps } from "./strokes";

/** Per-link motion state; every field is optional. */
export interface EdgeMotion {
  /** 0..1 draw-in from the link's first word toward its second. */
  draw?: number;
  color?: string;
  width?: number;
  /** strokeDashoffset of the shimmer overlay, or undefined for none. */
  shimmerOffset?: number;
}

interface BoardEdgesProps {
  links: BoardLink[];
  pos: Map<string, { px: number; py: number }>;
  palette: BoardPalette;
  scale: number;
  won: boolean;
  motionOf?: (link: BoardLink) => EdgeMotion | undefined;
  unitsPerPx?: number;
}

const SHIMMER_DASH = "2 10";

const BoardEdges: React.FC<BoardEdgesProps> = ({
  links,
  pos,
  palette,
  scale,
  won,
  motionOf,
  unitsPerPx = 1,
}) => (
  <G>
    {links.map((link) => {
      const a = pos.get(link.a);
      const b = pos.get(link.b);
      if (!a || !b) return null;
      const st = edgeStyle(link, { palette, scale, won });
      const m = motionOf?.(link);
      const t = m?.draw ?? 1;
      const g = {
        x1: a.px,
        y1: a.py,
        x2: a.px + (b.px - a.px) * t,
        y2: a.py + (b.py - a.py) * t,
        strokeLinecap: "round" as const,
      };
      const width = m?.width ?? st.width;
      return (
        <G key={link.key}>
          <Line
            {...g}
            stroke={BOARD_BG}
            {...strokeWidthProps(
              width + (st.casingWidth - st.width),
              unitsPerPx,
            )}
          />
          <Line
            {...g}
            stroke={m?.color ?? st.color}
            strokeDasharray={st.dash}
            {...strokeWidthProps(width, unitsPerPx)}
          />
          {st.rail !== null && (
            <Line
              {...g}
              stroke={BOARD_BG}
              {...strokeWidthProps(st.rail, unitsPerPx)}
            />
          )}
          {m?.shimmerOffset !== undefined && (
            <Line
              {...g}
              stroke="#FFFFFF"
              strokeDasharray={SHIMMER_DASH}
              strokeDashoffset={m.shimmerOffset}
              {...strokeWidthProps(1.2, unitsPerPx)}
            />
          )}
        </G>
      );
    })}
  </G>
);

export default BoardEdges;
